import { useState } from "react";
import { useForm } from "react-hook-form";
import AgentLayout from "@/layouts/AgentLayout";
import { useToast } from "@/hooks/use-toast";
import {
  useInviteAgent,
  useListAgentInvitations,
  useDeleteAgentInvitation,
} from "@/lib/api-client-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Trash2, Users, Mail, FileDigit, Percent, UserPlus, Clock, CheckCircle2 } from "lucide-react";
import { motion, Variants } from "framer-motion";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function AddAgent() {
  const { toast } = useToast();
  const inviteMutation = useInviteAgent();
  const deleteMutation = useDeleteAgentInvitation();
  const { data: invitations, isLoading, refetch } = useListAgentInvitations();

  const [serverError, setServerError] = useState<string | null>(null);
  const { register, handleSubmit, reset, formState: { errors } } = useForm();

  const fadeUp: Variants = {
    hidden: { opacity: 0, y: 15 },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.1, duration: 0.4, ease: "easeOut" },
    }),
  };

  const pendingCount = invitations?.data?.filter((inv: any) => inv.status === 'PENDING').length || 0;
  const acceptedCount = invitations?.data?.filter((inv: any) => inv.status === 'ACCEPTED').length || 0;
  const totalCount = invitations?.data?.length || 0;

  const handleDelete = (id: string) => {
    deleteMutation.mutate(id, {
      onSuccess: () => {
        toast({ title: "Removed", description: "Agent has been removed from your team." });
      },
      onError: (error: any) => {
        toast({ title: "Error", description: error.message, variant: "destructive" });
      }
    });
  };

  const onSubmit = (data: any) => {
    setServerError(null);
    inviteMutation.mutate(
      { email: data.email, agentNumber: data.agentNumber },
      {
        onSuccess: (res: any) => {
          toast({
            title: "Invitation Sent",
            description: `An invitation has been sent to ${data.email}.`,
          });
          reset();
          refetch();
        },
        onError: (error: any) => {
          setServerError(error.message);
        }
      }
    );
  };

  return (
    <AgentLayout>
      <div className="w-full p-6 sm:p-8 lg:p-10 xl:px-12 sm:lg:xl:sm:lg:sm:lg:xl:container">
        <motion.div custom={0} variants={fadeUp} initial="hidden" animate="visible" className="flex items-center gap-3 mb-8">
          <div className="p-3 bg-primary/10 rounded-xl">
            <Users className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h1 className="text-3xl font-bold">My Team</h1>
            <p className="text-muted-foreground mt-1">Manage your group practice members and invitations.</p>
          </div>
        </motion.div>

        {/* Stats Section */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {[
            { label: "Total Members", value: isLoading ? "—" : totalCount, icon: Users, color: "text-blue-600", bg: "bg-blue-50 dark:bg-blue-900/20" },
            { label: "Active Agents", value: isLoading ? "—" : acceptedCount, icon: CheckCircle2, color: "text-green-600", bg: "bg-green-50 dark:bg-green-900/20" },
            { label: "Pending Invites", value: isLoading ? "—" : pendingCount, icon: Clock, color: "text-amber-600", bg: "bg-amber-50 dark:bg-amber-900/20" },
          ].map((stat, i) => (
            <motion.div key={stat.label} custom={i + 1} variants={fadeUp} initial="hidden" animate="visible">
              <div className="bg-card rounded-2xl border border-border p-5 shadow-xs hover:shadow-sm transition-shadow">
                <div className="flex items-center justify-between mb-3">
                  <div className={`w-10 h-10 rounded-xl ${stat.bg} flex items-center justify-center`}>
                    <stat.icon className={`w-5 h-5 ${stat.color}`} />
                  </div>
                </div>
                {isLoading ? <Skeleton className="h-7 w-12 mb-1" /> : <p className="text-2xl font-bold">{stat.value}</p>}
                <p className="text-xs text-muted-foreground">{stat.label}</p>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Invite Form */}
          <motion.div custom={2} variants={fadeUp} initial="hidden" animate="visible" className="col-span-1">
            <Card className="rounded-2xl shadow-xs border-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <UserPlus className="w-5 h-5 text-primary" />
                  Invite Agent
                </CardTitle>
                <CardDescription>Add a new agent to your practice.</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="flex items-center gap-2 text-muted-foreground">
                      <Mail className="w-4 h-4" /> Email Address
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      className="rounded-xl bg-muted/30 focus:bg-background transition-colors"
                      placeholder="agent@example.com"
                      {...register("email", { required: "Email is required" })}
                    />
                    {errors.email && <p className="text-sm text-red-500">{String(errors.email.message)}</p>}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="agentNumber" className="flex items-center gap-2 text-muted-foreground">
                      <FileDigit className="w-4 h-4" /> Agent Number
                    </Label>
                    <Input
                      id="agentNumber"
                      className="rounded-xl bg-muted/30 focus:bg-background transition-colors"
                      placeholder="AGT-123456"
                      {...register("agentNumber", { required: "Agent Number is required" })}
                    />
                    {errors.agentNumber && <p className="text-sm text-red-500">{String(errors.agentNumber.message)}</p>}
                  </div>

                  {/* Percentage removed for agents */}

                  {serverError && (
                    <div className="p-3 text-sm text-red-600 bg-red-50 border border-red-100 rounded-md">
                      {serverError}
                    </div>
                  )}

                  <Button type="submit" className="w-full rounded-xl" disabled={inviteMutation.isPending}>
                    {inviteMutation.isPending ? "Sending..." : "Send Invitation"}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </motion.div>

          {/* Invitations List */}
          <motion.div custom={3} variants={fadeUp} initial="hidden" animate="visible" className="col-span-1 md:col-span-2">
            <Card className="rounded-2xl shadow-xs border-border h-full">
              <CardHeader>
                <CardTitle>Sent Invitations</CardTitle>
                <CardDescription>Track the status of your team members.</CardDescription>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <p>Loading...</p>
                ) : invitations?.data && invitations.data.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Email</TableHead>
                        <TableHead>Agent Number</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="w-[100px] text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {invitations.data.map((inv: any) => (
                        <TableRow key={inv.id}>
                          <TableCell>{inv.email}</TableCell>
                          <TableCell>{inv.agentNumber}</TableCell>
                          <TableCell>
                            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${inv.status === 'ACCEPTED' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                              }`}>
                              {inv.status}
                            </span>
                          </TableCell>
                          <TableCell className="text-right">
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                  title="Remove from team"
                                  disabled={deleteMutation.isPending}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Remove Agent</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Are you sure you want to remove this agent from your team? This action cannot be undone.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => handleDelete(inv.id)} className="bg-red-500 hover:bg-red-600 text-white">
                                    Remove
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-16 text-muted-foreground border border-dashed rounded-2xl bg-muted/10 flex flex-col items-center justify-center">
                    <div className="w-12 h-12 bg-primary/5 rounded-full flex items-center justify-center mb-4">
                      <Users className="w-6 h-6 text-primary/40" />
                    </div>
                    <h3 className="font-semibold text-foreground mb-1">No team members yet</h3>
                    <p className="text-sm max-w-sm">Use the form on the left to invite agents to join your group practice.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </AgentLayout>
  );
}
